import { useState, useEffect } from "react";
import { API_BASE_URL } from "../services/api";
import Sidebar from "./Sidebar";
import { useEventLogger } from "../hooks/useEventLogger";

export default function UserAnalytics({ user, onPageChange }) {
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("today");
  const { logNavigation } = useEventLogger(user);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const fetchAnalytics = async () => {
    if (dateRange === "custom" && !selectedDate) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("companioToken");
      let url = `${API_BASE_URL}/admin/analytics?range=${dateRange}`;
      if (dateRange === "custom") {
        url += `&date=${selectedDate}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.users || []);
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, selectedDate]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="workspace">
      <Sidebar
        user={user}
        currentPage="analytics"
        onPageChange={onPageChange}
        title={user?.name || user?.email}
        subtitle="User Analytics"
        onNavigation={logNavigation}
      />

      <div className="annotator">
        <div className="annotator-header">
          <h1>User Analytics</h1>

          <div
            className="date-filter"
            style={{ display: "flex", alignItems: "center", gap: "12px" }}
          >
            {dateRange === "custom" && (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="date-select"
                max={new Date().toISOString().split("T")[0]}
              />
            )}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="date-select"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Date</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading analytics...</div>
        ) : (
          <div className="analytics-container">
            <div className="stats-cards">
              <div className="stat-card">
                <div className="stat-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="stat-content">
                  <div className="stat-value">{analytics.length}</div>
                  <div className="stat-label">Total Users</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14 2V8H20"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16 13H8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16 17H8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 9H9H8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="stat-content">
                  <div className="stat-value">
                    {analytics.reduce(
                      (sum, user) => sum + user.annotations_count,
                      0
                    )}
                  </div>
                  <div className="stat-label">Total Annotations</div>
                </div>
              </div>
            </div>

            <div className="analytics-table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Annotations</th>
                    <th>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.map((userStats) => (
                    <tr key={userStats.email}>
                      <td>
                        <div className="user-info">
                          <img
                            src={
                              userStats.picture ||
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                userStats.name || userStats.email
                              )}&background=f4d9cc&color=7a2c16&size=32`
                            }
                            alt={userStats.name || userStats.email}
                            className="user-avatar"
                          />
                          <div>
                            <div className="user-name">
                              {userStats.name || userStats.email}
                            </div>
                            <div className="user-email">{userStats.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="annotations-count">
                        {userStats.annotations_count}
                      </td>
                      <td className="last-activity">
                        {formatDateTime(userStats.last_activity)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {analytics.length === 0 && (
                <div className="no-data">
                  No user activity found for the selected period.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
